'use client';

import { useTranslations } from 'next-intl';
import { H2Large } from './styleConstants';
import useEmblaCarousel from 'embla-carousel-react';
import { RightArrow } from '../../assets/icons';
import { VideoPlayer, ProductMenuButtonAndOverlay } from '../shared';
import { useEffect, useState, useCallback } from 'react';

import {
  experienceData,
  globalExperiences,
  indianExperiences,
} from '../../utils/data';
import { SPEEDSIZE_CDN, S3_URI } from 'lib/config/assetUrls';
import styles from './ExperienceVideoCarouselSection.module.css';

const CarousalElement = ({
  isPlayable,
  data,
}: {
  isPlayable: boolean;
  data: Record<string, string>;
}) => {
  const SPEEDSIZE_CLIENT_ID = process.env.NEXT_PUBLIC_SPEEDSIZE_CLIENT_ID;

  return (
    <div className={styles.carousalElement}>
      <div className={styles.elementContent}>
        <VideoPlayer
          smallBtn={true}
          playable={isPlayable}
          borderLessControls={true}
          videoProps={{
            src: data.vidSrc,
            poster: `${SPEEDSIZE_CDN}/${SPEEDSIZE_CLIENT_ID}/${S3_URI}/${data.poster}`,
            preload: 'true',
          }}
        />
        <div className={styles.testimonial}>
          <div className={styles.message}>{data?.message}</div>
          <div className={styles.author}>- {data?.name}</div>
        </div>
      </div>
    </div>
  );
};

export const ExperienceVideoCarouselSection = ({
  showButton = false,
}: {
  showButton?: boolean;
}) => {
  const t = useTranslations('home');
  const [activeSlide, setActiveSlide] = useState<number | string | undefined>(
    0,
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [experiencesData, setExperiencesData] = useState<experienceData[]>([]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    startIndex: 0,
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const index = emblaApi.selectedScrollSnap();
    setActiveSlide(index);
    setSelectedIndex(index);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    const timezoneOffset = new Date().getTimezoneOffset();
    const data =
      timezoneOffset === -330 ? indianExperiences : globalExperiences;
    setExperiencesData(data);
  }, []);

  return (
    <div className={`${styles.root} u-grid`}>
      <div className={styles.content}>
        <H2Large color="black">
          {t('home.experienceVideoCarouselSection.heading.realCyborgs')}
        </H2Large>
        {showButton ? (
          <ProductMenuButtonAndOverlay
            btnType="outline"
            className={styles.productOverlayButton}
            btnTitle={t('home.experienceVideoCarouselSection.button.getStarted')}
          />
        ) : null}
      </div>
      <div className={styles.textCarousal}>
        <div className={styles.carousalContainer}>
          <div className={styles.embla} ref={emblaRef}>
            <div className={styles.emblaContainer}>
              {experiencesData.map((element, index) => {
                return (
                  <div
                    key={index}
                    className={`${styles.emblaSlide} ${
                      selectedIndex === index ? styles.emblaSlideSelected : ''
                    }`}
                  >
                    <CarousalElement
                      data={element}
                      isPlayable={activeSlide == index}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <button
            className={styles.emblaPrev}
            aria-label="Previous slide"
            onClick={scrollPrev}
          >
            <div className={styles.circleBlurBtn}>
              <RightArrow style={{ transform: 'rotate(180deg)' }} />
            </div>
          </button>
          <button
            className={styles.emblaNext}
            aria-label="Next slide"
            onClick={scrollNext}
          >
            <div className={styles.circleBlurBtn}>
              <RightArrow />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};
